/**
 * facet-variant-availability.js
 *
 * Progressive enhancement for the theme's `facet-form` filter form.
 *
 * Problem: filtering by a variant option (`filter.v.option.*` — Farbe, Speicher,
 * Zustand, …) still returns products whose variants matching that option are all sold
 * out, because Shopify only narrows to "has an in-stock variant matching that option"
 * when `filter.v.availability=1` is ALSO present (both are variant-level filters,
 * combined at variant granularity).
 *
 * Behavior:
 *   - When >=1 variant-option checkbox (any input whose name starts with
 *     `filter.v.option.`) is checked in a facet-form AND the user has not set the
 *     availability control themselves, inject a hidden
 *     <input name="filter.v.availability" value="1" data-auto-availability> so the
 *     form serializes availability=1.
 *   - When the last variant-option checkbox is unchecked, remove ONLY our auto-injected
 *     input. A user-chosen availability control is never touched.
 *
 * Ordering: the theme's FacetForm registers a bubble-phase `change` listener on the
 * form (constructor) whose handler serializes via `new FormData(this)` synchronously
 * (either through requestSubmit() -> _onFormSubmitted -> _buildUrl, or directly via
 * _buildUrl in cachedFetch). We listen on `document` in the CAPTURE phase, so our
 * handler runs before the form's bubble-phase handler and the hidden input is present
 * at serialization time.
 *
 * Delegation on `document` (not per-form) is deliberate: after each filter update the
 * theme replaces the whole section DOM (shopifySection.replaceChildren), which would
 * drop any per-element listeners. A document-level capture listener survives that.
 *
 * No JS => no listener => no injection => identical no-availability behavior.
 */
(function () {
  'use strict';

  // The snippet renders in both the desktop sidebar and mobile drawer, so this file
  // may be included more than once. Register the document listener only once.
  if (window.__facetVariantAvailabilityInit) {
    return;
  }
  window.__facetVariantAvailabilityInit = true;

  var OPTION_PREFIX = 'filter.v.option.';
  var AVAILABILITY_NAME = 'filter.v.availability';
  var AUTO_ATTR = 'data-auto-availability';

  /**
   * Returns true for a variant-option control (any input whose name starts with
   * `filter.v.option.`, e.g. Farbe/Speicher/Zustand).
   */
  function isOptionControl(name) {
    return typeof name === 'string' && name.indexOf(OPTION_PREFIX) === 0;
  }

  /**
   * Returns true if at least one variant-option checkbox is currently checked in the
   * form (any filter.v.option.* control).
   */
  function hasCheckedOption(form) {
    var boxes = form.querySelectorAll('input[name^="' + OPTION_PREFIX + '"]');
    for (var i = 0; i < boxes.length; i++) {
      if (boxes[i].checked) {
        return true;
      }
    }
    return false;
  }

  /**
   * Returns true if the user has an explicit, active availability control in the form
   * (a real availability checkbox that is checked, or any availability input that we
   * did NOT auto-inject). We must never override such a choice.
   */
  function hasUserAvailability(form) {
    var inputs = form.querySelectorAll(
      'input[name="' + AVAILABILITY_NAME + '"]'
    );
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      if (input.hasAttribute(AUTO_ATTR)) {
        continue;
      }
      // A checkbox/radio counts only when checked; hidden/other inputs count as set.
      if (input.type === 'checkbox' || input.type === 'radio') {
        if (input.checked) {
          return true;
        }
      } else {
        return true;
      }
    }
    return false;
  }

  /**
   * Removes any availability input we previously auto-injected into this form.
   */
  function removeAutoInput(form) {
    var autos = form.querySelectorAll(
      'input[name="' + AVAILABILITY_NAME + '"][' + AUTO_ATTR + ']'
    );
    for (var i = 0; i < autos.length; i++) {
      autos[i].parentNode.removeChild(autos[i]);
    }
  }

  /**
   * Ensures a single auto-injected availability=1 hidden input is present in the form.
   */
  function ensureAutoInput(form) {
    var existing = form.querySelector(
      'input[name="' + AVAILABILITY_NAME + '"][' + AUTO_ATTR + ']'
    );
    if (existing) {
      return;
    }
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = AVAILABILITY_NAME;
    input.value = '1';
    input.setAttribute(AUTO_ATTR, '');
    form.appendChild(input);
  }

  /**
   * Reconciles the auto-availability input for the given form based on current state.
   * Runs synchronously so the DOM is correct before FacetForm serializes.
   */
  function reconcile(form) {
    // Never override an explicit user availability choice: drop our injection if any.
    if (hasUserAvailability(form)) {
      removeAutoInput(form);
      return;
    }

    if (hasCheckedOption(form)) {
      ensureAutoInput(form);
    } else {
      removeAutoInput(form);
    }
  }

  /**
   * Capture-phase change handler on document. Fires before the form's own bubble-phase
   * change listener, so our injected/removed input is in place at serialization time.
   */
  function onChangeCapture(event) {
    var target = event.target;
    if (!target || !target.name) {
      return;
    }
    // Only react to changes on a variant-option or the availability control.
    if (!isOptionControl(target.name) && target.name !== AVAILABILITY_NAME) {
      return;
    }
    var form = target.form || (target.closest ? target.closest('form') : null);
    if (!form || form.getAttribute('is') !== 'facet-form') {
      return;
    }
    reconcile(form);
  }

  document.addEventListener('change', onChangeCapture, true);
})();
